<template>
  <div :class="[props.class, 'picker-wrapper']" ref="pickerRef">
    <!-- 触发器 -->
    <div
      class="picker-trigger"
      :class="{
        'picker-trigger--disabled': disabled
      }"
      @click="showPicker"
    >
      <span
        class="picker-value"
        :class="{ 'picker-value--placeholder': !displayText }"
      >
        {{ displayText || placeholder }}
      </span>
      <ChevronRight :size="16" class="picker-icon text-gray-400" />
    </div>

    <!-- 移动端选择器弹窗 -->
    <div
      v-if="isPickerVisible"
      class="fixed inset-0 z-50 flex items-end justify-center picker-overlay"
      @click="hidePicker"
    >
      <div
        class="bg-white rounded-t-2xl w-full max-w-md mx-4 mb-0 animate-slide-up picker-panel"
        @click.stop
      >
        <!-- 弹窗标题栏 -->
        <div class="p-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold text-gray-800 flex-1 text-center">
              {{ title || '请选择' }}
            </h3>
            <!-- 多选时显示全选/取消全选 -->
            <button
              v-if="multiple"
              @click="toggleSelectAll"
              class="text-sm text-blue-500 font-medium hover:text-blue-600 transition-colors"
            >
              {{ isAllSelected ? '取消全选' : '全选' }}
            </button>
          </div>
        </div>

        <!-- 选项列表 -->
        <div class="max-h-80 overflow-y-auto">
          <div
            v-for="option in normalizedOptions"
            :key="option.value"
            class="p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
            @click="selectOption(option)"
          >
            <!-- 多选保持原有布局 -->
            <template v-if="multiple">
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <div class="text-sm font-medium text-gray-800">
                    {{ option.label }}
                  </div>
                  <div
                    v-if="option.description"
                    class="text-xs text-gray-500 mt-1"
                  >
                    {{ option.description }}
                  </div>
                </div>
                <div class="flex-shrink-0 ml-3">
                  <div
                    class="w-5 h-5 rounded border-2 flex items-center justify-center transition-colors"
                    :class="
                      isOptionSelected(option)
                        ? 'bg-blue-500 border-blue-500'
                        : 'border-gray-300'
                    "
                  >
                    <Check
                      v-if="isOptionSelected(option)"
                      :size="14"
                      class="text-white"
                    />
                  </div>
                </div>
              </div>
            </template>

            <!-- 单选使用相对布局，文本左对齐，勾选图标绝对定位到右侧 -->
            <template v-else>
              <div class="picker-option-relative">
                <div class="flex items-center justify-start">
                  <div class="flex-1 pr-10">
                    <div class="text-sm font-medium text-gray-800 text-left">
                      {{ option.label }}
                    </div>
                    <div
                      v-if="option.description"
                      class="text-xs text-gray-500 mt-1"
                    >
                      {{ option.description }}
                    </div>
                  </div>
                </div>
                <div class="picker-option-check">
                  <div v-if="isOptionSelected(option)" class="text-blue-500">
                    <Check :size="20" />
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div class="p-4 border-t border-gray-200">
          <div v-if="multiple" class="flex space-x-3">
            <button
              @click="hidePicker"
              class="flex-1 bg-gray-500 text-white py-2.5 rounded-lg text-sm font-medium transition-colors hover:bg-gray-600"
            >
              取消
            </button>
            <button
              @click="confirmSelection"
              class="flex-1 bg-blue-500 text-white py-2.5 rounded-lg text-sm font-medium transition-colors hover:bg-blue-600"
              :disabled="tempSelectedValues.length === 0"
              :class="{
                'opacity-50 cursor-not-allowed': tempSelectedValues.length === 0
              }"
            >
              确认 ({{ tempSelectedValues.length }})
            </button>
          </div>
          <button
            v-else
            @click="hidePicker"
            class="w-full bg-gray-500 text-white py-2.5 rounded-lg text-sm font-medium transition-colors hover:bg-gray-600"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { ChevronRight, Check } from 'lucide-vue-next'

export type Value = string | number
type MultipleValue = Value[]

interface PickerOption {
  value?: Value
  label?: string
  description?: string
  [key: string]: any
}

interface NormalizedPickerOption {
  value: Value
  label: string
  description?: string
  _original?: PickerOption
}

interface Props {
  modelValue?: Value | MultipleValue
  options: PickerOption[]
  placeholder?: string
  title?: string
  disabled?: boolean
  multiple?: boolean
  class?: string
  labelKey?: string
  valueKey?: string
}

interface Emits {
  (e: 'update:modelValue', value: Value | MultipleValue): void
  (
    e: 'change',
    value: Value | MultipleValue,
    option?: PickerOption | PickerOption[]
  ): void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  placeholder: '请选择',
  title: '',
  disabled: false,
  multiple: false,
  class: '',
  labelKey: 'label',
  valueKey: 'value'
})

const emit = defineEmits<Emits>()

const pickerRef = ref<HTMLElement>()
const isPickerVisible = ref(false)
const tempSelectedValues = ref<Value[]>([])

// 标准化选项数据，支持别名
const normalizedOptions = computed((): NormalizedPickerOption[] => {
  return props.options.map(option => ({
    value: option[props.valueKey!] as Value,
    label: option[props.labelKey!] as string,
    description: option.description,
    _original: option // 保存原始数据引用
  }))
})

// 计算当前选中的值数组（用于多选）
const selectedValues = computed(() => {
  if (props.multiple) {
    return Array.isArray(props.modelValue) ? props.modelValue : []
  }
  return props.modelValue !== undefined ? [props.modelValue] : []
})

// 计算当前选中的选项（用于单选）
const selectedOption = computed(() => {
  if (props.multiple) return null
  return normalizedOptions.value.find(
    option => option.value === props.modelValue
  )
})

// 计算显示文本
const displayText = computed(() => {
  if (props.multiple) {
    const count = selectedValues.value.length
    if (count === 0) return ''
    if (count === 1) {
      const option = normalizedOptions.value.find(
        o => o.value === selectedValues.value[0]
      )
      return option?.label || ''
    }
    return `已选择 ${count} 项`
  } else {
    return selectedOption.value?.label || ''
  }
})

// 计算是否全选
const isAllSelected = computed(() => {
  if (!props.multiple) return false
  return tempSelectedValues.value.length === normalizedOptions.value.length
})

// 显示选择器
const showPicker = () => {
  if (props.disabled) return

  // 初始化临时选中值
  if (props.multiple) {
    tempSelectedValues.value = Array.isArray(props.modelValue)
      ? [...props.modelValue]
      : []
  } else {
    tempSelectedValues.value =
      props.modelValue !== undefined && !Array.isArray(props.modelValue)
        ? [props.modelValue]
        : []
  }
  isPickerVisible.value = true

  // 防止背景滚动
  document.body.style.overflow = 'hidden'
}

// 隐藏选择器
const hidePicker = () => {
  isPickerVisible.value = false

  // 恢复背景滚动
  document.body.style.overflow = ''
}

// 判断选项是否被选中
const isOptionSelected = (option: NormalizedPickerOption): boolean => {
  if (props.multiple) {
    return tempSelectedValues.value.includes(option.value)
  }
  return option.value === props.modelValue
}

// 选择选项
const selectOption = (option: NormalizedPickerOption) => {
  if (props.multiple) {
    // 多选模式：切换选中状态
    const index = tempSelectedValues.value.indexOf(option.value)
    if (index > -1) {
      tempSelectedValues.value.splice(index, 1)
    } else {
      tempSelectedValues.value.push(option.value)
    }
  } else {
    // 单选模式：直接选中并关闭
    emit('update:modelValue', option.value)
    emit('change', option.value, option._original || option)
    hidePicker()
  }
}

// 切换全选状态
const toggleSelectAll = () => {
  if (isAllSelected.value) {
    tempSelectedValues.value = []
  } else {
    tempSelectedValues.value = normalizedOptions.value.map(
      option => option.value
    )
  }
}

// 确认多选结果
const confirmSelection = () => {
  const selectedOptions = normalizedOptions.value
    .filter(option => tempSelectedValues.value.includes(option.value))
    .map(option => option._original || option)

  emit('update:modelValue', tempSelectedValues.value)
  emit('change', tempSelectedValues.value, selectedOptions)
  hidePicker()
}
</script>

<style scoped>
.picker-wrapper {
  position: relative;
  flex: 1;
}

.picker-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 44px; /* iOS 推荐的最小触摸目标 */
}

.picker-trigger:active {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  transform: scale(0.98);
}

.picker-trigger--disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f1f5f9;
}

.picker-trigger--disabled:active {
  background: #f1f5f9;
  transform: none;
}

.picker-value {
  flex: 1;
  text-align: left;
  color: #374151;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.picker-value--placeholder {
  color: #9ca3af;
  font-weight: 400;
}

.picker-icon {
  flex-shrink: 0;
  margin-left: 8px;
  transition: transform 0.2s ease;
}

/* 滑入动画 */
.animate-slide-up {
  animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* iOS 风格滚动 */
.overflow-y-auto {
  -webkit-overflow-scrolling: touch;
}

/* 滚动条样式 */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* 背景蒙层样式优化（更柔和、带模糊） */
.picker-overlay {
  background: rgba(2, 6, 23, 0.12); /* 轻微遮罩，避免过暗 */
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  transition:
    background-color 0.18s ease,
    opacity 0.18s ease;
}

/* 弹窗面板微调，降低突兀的阴影与硬边 */
.picker-panel {
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(249, 250, 252, 0.98) 100%
  );
  border: 1px solid rgba(15, 23, 42, 0.04);
  box-shadow: 0 -8px 28px -8px rgba(2, 6, 23, 0.08);
}

/* 弹窗圆角和阴影优化 */
.bg-white.rounded-t-2xl {
  /* 保留后备样式，但已由 .picker-panel 覆盖为更柔和的样式 */
  box-shadow:
    0 -10px 25px -5px rgba(0, 0, 0, 0.06),
    0 -4px 6px -2px rgba(0, 0, 0, 0.03);
}

/* 选项项触摸优化 */
.cursor-pointer.hover\:bg-gray-50 {
  -webkit-tap-highlight-color: transparent;
}

.cursor-pointer.hover\:bg-gray-50:active {
  background-color: #f1f5f9;
  transform: scale(0.98);
  transition: all 0.1s ease;
}

/* 按钮触摸反馈 */
.bg-gray-500:active {
  transform: scale(0.98);
  transition: all 0.1s ease;
}

/* 单选模式：确保文本左对齐，并将勾选图标固定在右侧 */
.picker-option-relative {
  position: relative;
}

.picker-option-relative .picker-option-check {
  position: absolute;
  right: 16px; /* 与内边距对齐 */
  top: 50%;
  transform: translateY(-50%);
}

.picker-option-relative .text-left {
  text-align: left;
}
</style>
